import { computed } from "vue";
import { useImmer } from "./useImmer";
import { toValue } from "@erp/shared/utils/to-value";
import { debounce } from "es-toolkit";
import { useState } from "./useState";
import { useBasicInfo } from "./useBasicInfo";
import { sanitizeString } from "../utils/format/trim";
/**
 * @description 유저 입력 관리 컴포저블
 * @param initialInputs 초기 유저 입력 값
 * @param debounceMs 디바운스 지연 시간 (밀리초, 기본값: 300ms)
 */
export const useForm = (initialInputs, debounceMs = 500) => {
    const initialInputsRef = toValue(initialInputs);
    const [inputs, update] = useImmer(initialInputsRef.value);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const { getProjectId, getClassificationId, getManufacturerId, getSpotId } = useBasicInfo();
    /**
     * @description 유저 입력 필드 값 변경(disabled, value 등)
     * @param key 유저 입력 키
     * @param field 필드 명
     * @param value 변경할 값
     */
    const updateField = (key, field, value) => {
        update((draft) => {
            if (Array.isArray(draft)) {
                const item = draft.find((item) => item.key === key);
                if (item) {
                    Object.assign(item, { [field]: value });
                }
            }
        });
    };
    /**
     * @description 유저 입력 값 변경(value 값만)
     * @param key 유저 입력 키
     * @param value 유저 입력 값
     */
    const updateInputs = (key, value) => {
        update((draft) => {
            if (Array.isArray(draft) && draft.find((item) => item.key === key)) {
                draft.find((item) => item.key === key).value = value;
            }
        });
    };
    /**
     * @description 유저 입력 값 설정 (디바운스 적용)
     * @param key 유저 입력 키
     * @param value 유저 입력 값
     */
    const debouncedSetInputs = debounce((key, value) => {
        updateInputs(key, value);
        setIsDebouncing(false);
    }, debounceMs);
    const setInputs = (key, value) => {
        setIsDebouncing(true);
        debouncedSetInputs(key, value);
    };
    /**
     * @description 특정 값에서 유저 입력 키와 일치하는 값을 찾아 설정
     * @param data 적용할 값이 담긴 객체
     */
    const applyInputs = (data) => {
        for (const input of inputs.value) {
            const key = input?.key ?? "";
            updateInputs(key, data[key] ?? "");
        }
    };
    const validateWithRules = (input) => {
        const rules = input.rules ?? [];
        const value = input.value;
        const messages = [];
        for (const rule of rules) {
            const result = rule(String(value));
            if (result !== true)
                messages.push(String(result));
        }
        return messages;
    };
    const validateField = (key) => {
        const input = inputs.value.find((input) => input.key === key);
        if (!input)
            return [];
        return validateWithRules(input);
    };
    /**
     * @description 유저 입력을 키-값 쌍으로 변환
     */
    const formData = computed(() => {
        const params = new Map();
        inputs.value.forEach((input) => {
            let value = input.value;
            if (input.type === "text") {
                value = sanitizeString(value);
            }
            params.set(input.key, value);
        });
        return Object.fromEntries(params);
    });
    /**
     * @description 유저 입력을 키-값 쌍으로 변환, query 파라미터 생성용
     */
    const paramsData = computed(() => {
        const params = new Map();
        inputs.value.forEach((input) => {
            // dateRange 타입인 경우
            if (Array.isArray(input.value) && input.type === "dateRange") {
                const [start, end] = input.value;
                // start가 없거나 빈 값인 경우
                if (!start || start.trim() === "") {
                    params.set("startDate", "");
                    params.set("endDate", "");
                    return;
                }
                const startDate = new Date(start);
                // Invalid Date 체크
                if (isNaN(startDate.getTime())) {
                    params.set("startDate", "");
                    params.set("endDate", "");
                    return;
                }
                // 시작일을 다음날로 설정(오늘 날짜 포함)
                startDate.setDate(startDate.getDate());
                const startFormatted = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")} 00:00:00`;
                // end가 있는 경우에만 endDate 처리
                if (end && end.trim() !== "") {
                    const endDate = new Date(end);
                    if (!isNaN(endDate.getTime())) {
                        // 종료일을 다음날로 설정(오늘 날짜 포함)
                        endDate.setDate(endDate.getDate());
                        const endFormatted = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")} 23:59:59:999`;
                        params.set("endDate", endFormatted);
                    }
                    else {
                        params.set("endDate", "");
                    }
                }
                else {
                    // end가 없는 경우 startDate만 설정
                    params.set("endDate", "");
                }
                params.set("startDate", startFormatted);
            }
            else if (input.type === "dateSingle" &&
                input.value &&
                input.value.trim() !== "") {
                const date = new Date(input.value);
                date.setDate(date.getDate()); // 하루 추가(오늘 날짜 포함)
                console.log(date);
                const pad = (n) => String(n).padStart(2, "0");
                const localDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} 23:59:59:999`;
                params.set(input.key, localDate);
                console.log(localDate);
            }
            else if (typeof input.value === "string" && input.value.trim() !== "") {
                const v = input.value.trim();
                if (input.key === "projectCode") {
                    params.set("projectId", String(getProjectId(v)) ?? "");
                    return;
                }
                if (input.key === "classification") {
                    params.set("classificationId", String(getClassificationId(v)) ?? "");
                    return;
                }
                if (input.key === "manufacturer") {
                    params.set("manufacturerId", String(getManufacturerId(v)) ?? "");
                    return;
                }
                if (input.key === "spot") {
                    params.set("spotId", String(getSpotId(v)) ?? "");
                    return;
                }
                params.set(input.key, v);
            }
        });
        return Object.fromEntries(params);
    });
    const resetForm = () => {
        inputs.value = initialInputsRef.value;
    };
    return {
        inputs,
        setInputs,
        updateField,
        updateInputs,
        applyInputs,
        resetForm,
        validateField,
        formData,
        paramsData,
        isDebouncing,
    };
};
